const MAX_VISIBLE_ELEMENTS = 7;
const MAX_FINISHED_IN_VIEW = 2;
const START_THRESHOLD = 3;
const END_THRESHOLD = 4;

interface CircleItem {
    isFinished: boolean;
}

interface VisibleElementsResult {
    visibleCircles: CircleItem[];
    collapsedStart: boolean;
    collapsedEnd: boolean;
}

const createCircleRange = (startIndex: number, endIndex: number, finishedStepsCount: number): CircleItem[] => {
    const length: number = endIndex - startIndex;

    return Array.from(
        {length},
        (_, index: number): CircleItem => ({
            isFinished: startIndex + index < finishedStepsCount,
        })
    );
};

const createComplexLayout = (stepsCount: number, finishedStepsCount: number): VisibleElementsResult => {
    let maxCircles: number = MAX_VISIBLE_ELEMENTS - 1; // Reserve space for one collapsed element

    // Early progress: show first circles + collapsed at end
    if (finishedStepsCount <= START_THRESHOLD) {
        return {
            visibleCircles: createCircleRange(0, maxCircles, finishedStepsCount),
            collapsedStart: false,
            collapsedEnd: true,
        };
    }

    // Late progress: show last circles + collapsed at start
    if (finishedStepsCount >= stepsCount - END_THRESHOLD) {
        const startIndex: number = stepsCount - maxCircles;

        return {
            visibleCircles: createCircleRange(startIndex, stepsCount, finishedStepsCount),
            collapsedStart: true,
            collapsedEnd: false,
        };
    }

    // Middle progress: show circles around finished steps
    // For exactly 8 steps, reserve space for 1 collapsed element (6 visible circles)
    // For more than 8 steps, reserve space for 2 collapsed elements (5 visible circles)
    maxCircles = stepsCount === 8 ? MAX_VISIBLE_ELEMENTS - 1 : MAX_VISIBLE_ELEMENTS - 2;
    const startIndex: number = Math.max(0, finishedStepsCount - MAX_FINISHED_IN_VIEW);
    const endIndex: number = startIndex + maxCircles;

    return {
        visibleCircles: createCircleRange(startIndex, endIndex, finishedStepsCount),
        collapsedStart: startIndex > 0,
        collapsedEnd: endIndex < stepsCount,
    };
};

// Calculate visible elements (main function)
export const calculateVisibleElements = (stepsCount: number, finishedStepsCount: number): VisibleElementsResult => {
    // Simple case: show all steps if 7 or fewer
    if (stepsCount <= MAX_VISIBLE_ELEMENTS) {
        return {
            visibleCircles: createCircleRange(0, stepsCount, finishedStepsCount),
            collapsedStart: false,
            collapsedEnd: false,
        };
    }

    // Complex case: need to fit exactly MAX_VISIBLE_ELEMENTS
    return createComplexLayout(stepsCount, finishedStepsCount);
};
